import { Card, CardHeader, CardBody } from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

interface ChatbotInfoProps {
  pageWidth: number;
}

export function ChatbotInfo({ pageWidth }: ChatbotInfoProps) {
  const { t } = useTranslation('education');

  const isMobile = pageWidth <= 860;

  return (
    <div className="flex flex-col items-center justify-center m-10 mt-4 mb-0 overflow-hidden">
      <div className="flex flex-row items-center gap-3">
        <span className="inline-block w-8 h-8 bg-primary-100 text-white rounded-full flex items-center justify-center text-small font-bold">
          <Icon
            icon="mdi:chat-processing-outline"
            className="text-primary-500 h-5 w-5"
          />
        </span>
        <h1 className="text-2xl font-bold">{t('common.link.answers')}</h1>
      </div>
      <p className="text-base text-center">{t('chatbotinfo.description')}</p>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          key="ai-warning"
        >
          <div
            className={`gap-2 grid w-full pt-4 ${isMobile ? 'grid-rows-3' : 'grid-cols-3'}`}
          >
            {_.times(3, (index) =>
              isMobile ? (
                <Card
                  shadow="none"
                  className="py-1 px-2 bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-900 m-0.5"
                  key={index}
                >
                  <CardBody className="flex flex-row items-center gap-2 pb-0 pt-1">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-small font-bold">
                      {index + 1}
                    </span>
                    <div className="flex flex-col">
                      <h4 className="text-medium font-bold dark:text-white">
                        {t(`chatbotinfo.card.item_title_${index + 1}`)}
                      </h4>
                      <p className="text-small text-gray-600 dark:text-gray-200">
                        {t(`chatbotinfo.card.item_text_${index + 1}`)}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              ) : (
                <Card
                  shadow="none"
                  className="text-center py-1 px-2 bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-900 m-0.5 gap-2"
                  key={index}
                >
                  <CardHeader className="flex flex-row items-center justify-center gap-3 pb-0">
                    <span className="w-7 h-7 bg-primary-500 text-white rounded-full flex items-center justify-center text-small font-bold">
                      {index + 1}
                    </span>
                    <h4 className="text-medium font-bold dark:text-white">
                      {t(`chatbotinfo.card.item_title_${index + 1}`)}
                    </h4>
                  </CardHeader>
                  <CardBody className="flex text-center pt-1">
                    <p className="text-small text-gray-600 dark:text-gray-200">
                      {t(`chatbotinfo.card.item_text_${index + 1}`)}
                    </p>
                  </CardBody>
                </Card>
              )
            )}
          </div>
          <Card
            shadow="none"
            className="col-span-3 text-center w-full py-1 px-2 bg-primary-100 rounded-xl border border-primary-100 my-4"
          >
            <CardHeader className="flex flex-row gap-2 pt-1 pb-0">
              <Icon
                icon="mdi:help-circle-outline"
                className="text-primary-500 w-4 h-4"
              />
              <h3 className="text-medium text-primary-600 font-bold">
                {t('chatbotinfo.list.title')}
              </h3>
            </CardHeader>
            <CardBody className="flex flex-col ">
              {_.times(3, (index) => (
                <div className="flex flex-row pb-2" key={index}>
                  <Icon
                    icon="mdi:check"
                    className="text-primary-500 w-4 h-4 mr-2 flex-shrink-0"
                  />
                  <span className="text-small">
                    <span className="text-small font-bold">
                      {t(`chatbotinfo.list.subtitle_${index + 1}`)}
                    </span>
                    {t(`chatbotinfo.list.text_${index + 1}`)}
                  </span>
                </div>
              ))}
            </CardBody>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
